'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { AnimatePresence, motion, useInView } from 'motion/react'
import Tooltip from '@/components/ui/Tooltip'
import Spotlight from '@/components/search/core/Spotlight'
import Overlay from '@/components/search/common/Overlay'
import RichInput from '@/components/search/core/RichInput'
import { AppCard } from '@/components/search/common/ChatApps'
import Suggestion from '@/components/search/common/Suggestion'
import ContextToggle from '@/components/search/common/ContextToggle'
import ScrollView from '@/components/ui/ScrollView'
import Menu from '@/components/ui/Menu'
import { Icon } from '@/icons/Icon'
import { cn, scrollToSection } from '@/lib/utils'
import { useHeaderContext } from '@/context/HeaderContext'
import { useChatContext } from '@/context/ChatContext'
import { getActiveInput, setActiveInput } from '@/lib/activeInput'
import { generateMenu } from '@/helpers/menus'
import type { App } from '@/payload-types'
import type { HomepageApp } from '@/app/(app)/fetchers'

type ChatToolbarButtonProps = {
  theme: 'primary' | 'ghost' | 'active' | 'disabled' | 'default'
  label?: string
  icon: ReactNode
  onClickAction?: () => void
}

type ChatActionsProps = {
  apps: HomepageApp[]
}

export default function ChatInput({ apps }: ChatActionsProps) {
  const [height, setHeight] = useState(120)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const pathname = usePathname()
  const isSearchPage = pathname?.startsWith('/search/')

  const { setTheme } = useHeaderContext()
  const {
    apps: selectedApps,
    showSearch,
    openSearch,
    closeSearch,
    showSpotlight,
    handleKeyTrigger,
    showSuggestion,
  } = useChatContext()

  const inView = useInView(containerRef, { amount: 0.5 })

  useEffect(() => {
    setTheme('light')
  }, [setTheme])

  useEffect(() => {
    if (selectedApps && selectedApps.length > 0) {
      setHeight(166)
    } else {
      setHeight(120)
    }
  }, [selectedApps])

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      // Skip "/" hotkey on search pages to avoid conflict with chat input
      if (isSearchPage && (event.key === '/' || event.code === 'Slash') && !event.shiftKey) {
        return
      }
      if (getActiveInput() === 'chat') {
        handleKeyTrigger(event)
      }
    }
    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [handleKeyTrigger, isSearchPage])

  useEffect(() => {
    const active = inView ? 'chat' : 'nav'
    setActiveInput(active)

    // Reset to 'nav' when component unmounts
    return () => {
      setActiveInput('nav')
    }
  }, [inView])

  useEffect(() => {
    // Scroll to top on mobile when search is opened
    if (showSearch) {
      const isMobile = window.innerWidth < 768
      if (isMobile) {
        scrollToSection({
          id: 'chat-input-container',
          offset: 80,
          animate: true,
          hideHash: true,
        })
      }
    }
  }, [showSearch])

  return (
    <div
      id="chat-input-container"
      ref={containerRef}
      className="relative w-full max-w-[calc(100vw-40px)] transition-[height] duration-300 ease-in-out md:max-w-[720px]"
      style={{ height: `${height}px` }}
    >
      <div className="relative z-30 h-fit w-full rounded-3xl border border-white bg-gradient-to-r from-[#FAFAFA] via-white to-[#FAFAFA] shadow-button">
        <div className="h-[70px] w-full" onClick={() => openSearch('chat')}>
          <RichInput />
        </div>
        <ChatAppSelection />
        <ChatActions apps={apps} />

        <AnimatePresence>{showSpotlight && <Spotlight type="chat" />}</AnimatePresence>
      </div>

      <AnimatePresence>{showSuggestion && <Suggestion />}</AnimatePresence>

      <AnimatePresence>
        {showSearch && <Overlay close={() => closeSearch()} z={10} />}
      </AnimatePresence>
    </div>
  )
}

export function ChatActions({ apps }: ChatActionsProps) {
  const {
    context,
    shortcuts,
    handleSubmit,
    searching,
    isMenuVisible,
    setIsMenuVisible,
    handleShortcutClick,
    addApp,
  } = useChatContext()

  const menu = generateMenu('chat', { apps: apps as App[], shortcuts, handleShortcutClick, addApp })

  return (
    <div className="flex items-center justify-between p-2">
      <div className="flex items-center gap-2">
        {menu && (
          <Tooltip text="Add apps for context" position="up" close={isMenuVisible}>
            <Menu
              items={menu}
              align="left"
              position="down"
              menuVisible={isMenuVisible}
              setMenuVisibleAction={setIsMenuVisible}
            >
              <ChatActionButton
                theme={isMenuVisible ? 'active' : 'default'}
                icon={<Icon name="plus" size={12} />}
                onClickAction={() => setIsMenuVisible(!isMenuVisible)}
              />
            </Menu>
          </Tooltip>
        )}
        <ContextToggle />
      </div>
      <div className="flex items-center gap-3">
        <Tooltip text="Dictate" position="up">
          <ChatActionButton theme="ghost" icon={<Icon name="microphone" size={12} />} />
        </Tooltip>

        {context === 'chat' ? (
          <ChatActionButton
            theme={searching ? 'disabled' : 'primary'}
            label={searching ? 'Searching...' : 'Ask AI'}
            icon={
              <Icon
                name={searching ? 'loader' : 'return-key'}
                className={searching ? 'animate-spin' : ''}
                size={12}
              />
            }
            onClickAction={() => !searching && handleSubmit()}
          />
        ) : (
          <ChatActionButton
            theme={searching ? 'disabled' : 'primary'}
            icon={
              <Icon
                name={searching ? 'loader' : 'arrow-right-small'}
                className={searching ? 'animate-spin' : ''}
                size={12}
              />
            }
            onClickAction={() => !searching && handleSubmit()}
          />
        )}
      </div>
    </div>
  )
}

export function ChatActionButton({ theme, label, icon, onClickAction }: ChatToolbarButtonProps) {
  let className = ''
  if (theme === 'primary') {
    className = 'bg-black text-white shadow-lg'
  } else if (theme === 'ghost') {
    className = 'text-gray-400 hover:bg-gray-100'
  } else if (theme === 'active') {
    className = 'bg-primary-100 text-primary-700'
  } else if (theme === 'disabled') {
    className = 'bg-gray-200 text-gray-600 cursor-not-allowed pointer-events-none'
  } else {
    className = 'bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-700'
  }

  return (
    <div
      className={cn(
        'flex h-8.5 w-8.5 cursor-pointer items-center justify-center gap-2 rounded-full text-14 font-medium transition-colors',
        label && 'w-fit px-3',
        className,
      )}
      onClick={onClickAction}
    >
      {label}
      {icon}
    </div>
  )
}

export function ChatAppSelection() {
  const { apps } = useChatContext()

  if (!apps || apps.length === 0) return null

  return (
    <AnimatePresence>
      {apps.length > 0 && (
        <motion.div
          key="chat-app-selection"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 46 }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
        >
          <ScrollView className="flex w-full gap-2 px-2">
            {apps
              .slice()
              .reverse()
              .map((app) => (
                <AppCard key={app.id} app={app} condensed={true} />
              ))}
          </ScrollView>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
